import { fabric } from "fabric"
import ScrewStainlessFixmethod from "./fixmethods/screw_stainless"
import { type DrawableMixin, type DrawableClasses } from "./objects/drawable_mixin"
import { clearLoadedImages } from "./with_images"
import { waitUntil, metaNameSign } from "@/common/util"
import Calc from "@/common/util/calculator"
import BallChainFixmethod from "@/signomatic/app/fixmethods/ball_chain"
import type Fixmethod from "@/signomatic/app/fixmethods/base"
import CableLabelFixmethod from "@/signomatic/app/fixmethods/cable_label"
import CableTiesFixmethod from "@/signomatic/app/fixmethods/cable_ties"
import CeilingMountFixmethod from "@/signomatic/app/fixmethods/ceiling_mount"
import CustomHolesFixmethod from "@/signomatic/app/fixmethods/custom_holes"
import EyeletsFixmethod from "@/signomatic/app/fixmethods/eyelets"
import FlagMountFixmethod from "@/signomatic/app/fixmethods/flag_mount"
import HangingFixmethod from "@/signomatic/app/fixmethods/hanging"
import KeyringFixmethod from "@/signomatic/app/fixmethods/keyring"
import MilitaryFixmethod from "@/signomatic/app/fixmethods/military"
import PoleFixmethod from "@/signomatic/app/fixmethods/pole"
import RollupFixmethod from "@/signomatic/app/fixmethods/rollup"
import SHookFixmethod from "@/signomatic/app/fixmethods/s_hook"
import ScrewAluFixmethod from "@/signomatic/app/fixmethods/screw_alu"
import ScrewBrassFixmethod from "@/signomatic/app/fixmethods/screw_brass"
import ScrewEcoboardFixmethod from "@/signomatic/app/fixmethods/screw_ecoboard"
import ScrewForexFixmethod from "@/signomatic/app/fixmethods/screw_forex"
import ScrewPlasticFixmethod from "@/signomatic/app/fixmethods/screw_plastic"
import ScrewSolidPlasticFixmethod from "@/signomatic/app/fixmethods/screw_solid_plastic"
import ScrewWoodFixmethod from "@/signomatic/app/fixmethods/screw_wood"
import SuctionCupsFixmethod from "@/signomatic/app/fixmethods/suction_cups"
import TableStandFixmethod from "@/signomatic/app/fixmethods/table_stand"
import WallDistanceFixmethod from "@/signomatic/app/fixmethods/wall_distance"
import WallProfileFixmethod from "@/signomatic/app/fixmethods/wall_profile"
import ArchedTopForm from "@/signomatic/app/forms/arched_top"
import ArchedTopBottomForm from "@/signomatic/app/forms/arched_top_bottom"
import ArrowLeftForm from "@/signomatic/app/forms/arrow_left"
import ArrowRightForm from "@/signomatic/app/forms/arrow_right"
import type Form from "@/signomatic/app/forms/base"
import BulgeForm from "@/signomatic/app/forms/bulge"
import ContourForm from "@/signomatic/app/forms/contour"
import CurvedTopForm from "@/signomatic/app/forms/curved_top"
import DirectionLeftForm from "@/signomatic/app/forms/direction_left"
import DirectionRightForm from "@/signomatic/app/forms/direction_right"
import EllipseForm from "@/signomatic/app/forms/ellipse"
import ParisForm from "@/signomatic/app/forms/paris"
import RectForm from "@/signomatic/app/forms/rect"
import RotatedRectForm from "@/signomatic/app/forms/rotated_rect"
import RoundIncutForm from "@/signomatic/app/forms/round_incut"
import RoundedSidesForm from "@/signomatic/app/forms/rounded_sides"
import RoundedTopForm from "@/signomatic/app/forms/rounded_top"
import StarForm from "@/signomatic/app/forms/star"
import TriangleForm from "@/signomatic/app/forms/triangle"
import type Sign from "@/signomatic/app/sign"
import SignOverlay, { type SignImagePatternOverlay, type SignImageOverlay } from "@/signomatic/app/sign_overlay"
import SignPattern from "@/signomatic/app/sign_pattern"
import { dataWithSignTypeOverride } from "@/signomatic/app/sign_validator"
import { ios } from "@/signomatic/util"

type SizeRelativeSide = "height" | "width" | "longest"

export enum DrawableMode {
  Preview,
  Print,
  Engraving,
}

export interface DrawableObject extends DrawableMixin {
  clipSign?: Sign
}

export interface RetroParams {
  strokeWidth: number
  distance: number
  radius: number
}

export default class SignDrawables {
  state: SignState
  signType: string
  readonly laserAdjustment = Calc.mmToPx(0.44)
  readonly engravingBorderOverflow = Calc.mmToPx(1)
  private form: Form
  private fixmethod: Fixmethod | null
  private pattern: SignPattern | null
  private baseOverlay: SignImagePatternOverlay | SignImageOverlay | null
  private readonly debugEngraving: boolean
  private readonly mode: DrawableMode
  private readonly overlay: SignOverlay
  private readonly sign: Sign

  constructor(sign: Sign, mode = DrawableMode.Preview, debugEngraving = false) {
    this.mode = mode
    this.sign = sign
    this.debugEngraving = debugEngraving
    this.overlay = new SignOverlay(this)

    this.setState(sign.state)
  }

  setState(newState: SignState) {
    if (!newState) {
      return
    }

    const typeChanged = this.signType !== this.sign.signType
    this.signType = this.sign.signType || ""

    const previousState = { ...this.state }
    this.state = { ...newState }
    const { color, shape, badgeName, fixmethod, material, doubleSided, side, pattern } = this.state

    if (!previousState || typeChanged) {
      this.fixmethod = this.createFixmethod(newState)
      this.form = this.createForm(newState)
      this.pattern = this.createPattern(newState)
      this.baseOverlay = this.createBaseOverlay(newState)
      return
    }

    if (fixmethod !== previousState.fixmethod || material !== previousState.material) {
      this.fixmethod = this.createFixmethod(newState)
    }

    if (
      shape !== previousState.shape ||
      badgeName !== previousState.badgeName ||
      (doubleSided && side !== previousState.side)
    ) {
      this.form = this.createForm(newState)
    }

    if (pattern !== previousState.pattern) {
      this.pattern = this.createPattern(newState)
    }

    if (
      color !== previousState.color ||
      badgeName !== previousState.badgeName ||
      material !== previousState.material ||
      shape !== previousState.shape
    ) {
      this.baseOverlay = this.createBaseOverlay(newState)
    }
  }

  redraw() {
    this.sign.redraw()
  }

  rerender() {
    this.sign.rerender()
  }

  /**
   * Calculate value in a range relative to current size. Options must be in mm.
   */
  sizeRelative(
    side: SizeRelativeSide,
    min: number,
    max: number,
    options?: { minWidth?: number; minHeight?: number; maxWidth?: number; maxHeight?: number }
  ): number {
    if (!this.sign) {
      return 0
    }

    const { min_size, max_size } = dataWithSignTypeOverride(this.sign.data, this.signType, this.state)
    const { width, height } = this.state
    const minWidth = (options && options.minWidth) || min_size.w
    const minHeight = (options && options.minHeight) || min_size.h
    const maxWidth = (options && options.maxWidth) || max_size.w
    const maxHeight = (options && options.maxHeight) || max_size.h
    let distancePercent = 0
    let measure = side

    if (measure === "longest") {
      measure = height > width ? "height" : "width"
    }

    if (measure === "height") {
      distancePercent = ((height - minHeight) * 100) / (maxHeight - minHeight)
    } else if (measure === "width") {
      distancePercent = ((width - minWidth) * 100) / (maxWidth - minWidth)
    }

    return ((max - min) / 100) * distancePercent + min
  }

  formDrawable(): DrawableObject {
    const drawable = this.form.base()

    if (drawable) {
      drawable.set({ name: "base" })
    }

    return drawable
  }

  baseDrawables(): DrawableObject[] {
    const drawables: DrawableObject[] = []

    if (this.drawBackplate) {
      const offsetDrawable = this.form.base(true, "offsetdrawable")
      if (offsetDrawable) {
        this.disableFabricCaching(offsetDrawable)
        drawables.push(this.decorateByClass(offsetDrawable))
      }

      const offsetOverlay = this.overlay.offsetOverlay(this.state)?.drawable()
      if (offsetOverlay) {
        this.disableFabricCaching(offsetOverlay)
        offsetOverlay.clipPath = this.sign.clipShape()
        this.addClasses(offsetOverlay, "offsetdrawable")
        offsetOverlay.set({ offsetX: this.offset.x, offsetY: this.offset.y })
        drawables.push(offsetOverlay)
      }
    }

    if (!this.isEngraving && this.fixmethod) {
      const bottomFixmethod = this.fixmethod.bottomDrawable()

      if (bottomFixmethod) {
        this.disableFabricCaching(bottomFixmethod)
        this.ensureClasses(bottomFixmethod, "preview")
        drawables.push(bottomFixmethod)
      }
    }

    if (!this.isEngraving) {
      drawables.push(this.decorateByClass(this.form.shadowBase()))
    }

    if (!this.isEngraving || this.hasBorder) {
      drawables.push(this.decorateByClass(this.form.base()))
    }

    if (!this.isEngraving) {
      const pattern = this.pattern?.drawable()

      if (pattern) {
        this.disableFabricCaching(pattern)
        pattern.clipPath = this.sign.clipShape()
        drawables.push(pattern)
      }

      const overlay = this.baseOverlay?.drawable()

      if (overlay) {
        this.disableFabricCaching(overlay)
        overlay.clipPath = this.sign.clipShape()
        drawables.push(overlay)
      }
    }

    if (!this.isEngraving && this.fixmethod) {
      const fixDrawable = this.fixmethod.baseDrawable()

      if (fixDrawable) {
        this.disableFabricCaching(fixDrawable)
        this.ensureClasses(fixDrawable, "preview")
        drawables.push(fixDrawable)
      }
    }

    if (["brass_sign", "color_badge"].includes(this.signType) && this.sign.printAreaPadding() > 0) {
      drawables.push(this.decorateByClass(this.form.printAreaCutline(this.sign.printAreaPadding())))
    }

    return drawables
  }

  topDrawables(): DrawableMixin[] {
    const drawables: DrawableMixin[] = []

    const topOverlay = this.overlay.topDrawable(this.isEngraving)

    if (topOverlay) {
      if (this.isEngraving) {
        topOverlay.stroke = this.engravingTextColor
      }

      drawables.push(topOverlay)
    }

    const borderdouble = this.form.base()

    if (borderdouble && !(this.signType === "sticker" && this.state.shape === "contour")) {
      this.addClasses(borderdouble, "borderdouble")

      drawables.push(this.decorateByClass(borderdouble))
    }

    if (this.glassBorderWidth) {
      const glassBorder = this.form.base(true, "glassborder")
      this.disableFabricCaching(glassBorder)
      drawables.push(this.decorateByClass(glassBorder))
    }

    if (this.fixmethod) {
      const fixDrawables = this.fixmethodTopDrawables() || []

      fixDrawables.forEach(fixDrawable => {
        if (fixDrawable) {
          this.disableFabricCaching(fixDrawable)

          if (this.state.doubleSided) {
            if (this.state.side === 1) {
              fixDrawable.flipX = true
            }
          }

          drawables.push(fixDrawable)
        }
      })
    }

    drawables.push(this.decorateByClass(this.form.cutline(this.isEngraving ? this.laserAdjustment : 0)))

    const gappedCutline = this.form.cutlineGapped()
    if (gappedCutline) {
      drawables.push(this.decorateByClass(gappedCutline))
    }

    return drawables
  }

  cutlineDrawable(forClip?: boolean) {
    return this.form.cutline(this.isEngraving ? this.laserAdjustment : 0, forClip)
  }

  printAreaCutlineDrawable() {
    return this.form.printAreaCutline(this.sign.printAreaPadding())
  }

  autoSizePadding() {
    let padding = 25

    padding += this.formAutoSizePadding()

    if (this.fixmethod) {
      padding += this.fixmethod.autoSizePadding(this.state)
    }

    padding += this.borderWidth * 2

    if (this.state.border === "retro") {
      padding += this.sizeRelative("height", Calc.mmToPx(3), Calc.mmToPx(20))
    }

    return padding
  }

  fixmethodPadding() {
    if (this.fixmethod && this.fixmethod.autoSizePadding) {
      return this.fixmethod.autoSizePadding(this.state)
    }

    return 0
  }

  star(type: string, finalW: number, finalH: number) {
    const points: Point[] = []
    let radiuses: any[] = []
    let jags = 0
    let startRadius = 0

    switch (type) {
      case "stop":
        radiuses = [20, 20]
        jags = 4
        startRadius = 22.5
        break
      case "thorny":
        radiuses = [20, 18]
        jags = 30
        startRadius = 54
        break
      default:
        throw `Unknown type ${type}`
    }

    const radiusAdd = 360 / (jags * radiuses.length)
    let actualR = 0
    let x = 0
    let y = 0
    let counterRadius = 0
    let currentRadius = 0
    const minmax = { xmin: 0, xmax: 0, ymin: 0, ymax: 0 }

    for (let s = 0; s <= jags * radiuses.length + 1; s++) {
      if (counterRadius > radiuses.length - 1) {
        counterRadius = 0
      }

      currentRadius = radiuses[counterRadius]
      counterRadius++
      actualR = s * radiusAdd + startRadius

      // using inner
      const a = (actualR * Math.PI) / 180
      x = currentRadius * Math.cos(a)
      y = currentRadius * Math.sin(a)
      points.push({ x, y })

      if (x < minmax.xmin) {
        minmax.xmin = x
      }
      if (x > minmax.xmax) {
        minmax.xmax = x
      }
      if (y < minmax.ymin) {
        minmax.ymin = y
      }
      if (y > minmax.ymax) {
        minmax.ymax = y
      }
    }

    for (let i = 0; i < points.length; i++) {
      x = (points[i].x - minmax.xmin) / (minmax.xmax - minmax.xmin)
      y = (points[i].y - minmax.ymin) / (minmax.ymax - minmax.ymin)
      points[i] = { x: x * finalW - finalW / 2, y: y * finalH - finalH / 2 }
    }

    if (type === "stop") {
      points.pop()
      points.pop()
    }

    return points
  }

  clipOffsetY(def: number) {
    if (
      this.state.fixmethod &&
      ["hanging", "suction_cups", "s_hook"].includes(this.state.fixmethod) &&
      (this.externalTop || this.externalSides)
    ) {
      return 0
    }

    return def
  }

  waitImagesLoaded() {
    return Promise.all([
      waitUntil(() => !this.fixmethod || this.fixmethod.imagesLoaded),
      waitUntil(() => !this.pattern || this.pattern.imagesLoaded),
      waitUntil(() => !this.baseOverlay || this.baseOverlay.imagesLoaded),
    ])
  }

  reloadImages() {
    clearLoadedImages()
    this.fixmethod?.loadImages()
    this.pattern?.loadImages()
    this.baseOverlay?.loadImages()
    return this.waitImagesLoaded()
  }

  retroParams(): RetroParams {
    let strokeWidth = this.sizeRelative("height", Calc.mmToPx(2), Calc.mmToPx(35))
    let distance = this.sizeRelative("height", Calc.mmToPx(1.4), Calc.mmToPx(35))
    let radius = this.sizeRelative("height", Calc.mmToPx(5), Calc.mmToPx(110))

    if (["plastic_sign", "wood_sign"].indexOf(this.signType) >= 0) {
      strokeWidth = this.sizeRelative("height", Calc.mmToPx(1), Calc.mmToPx(6))
      distance = this.sizeRelative("height", Calc.mmToPx(1), Calc.mmToPx(6))
      radius = this.sizeRelative("height", Calc.mmToPx(3), Calc.mmToPx(20))
    }

    return { strokeWidth, distance, radius }
  }

  get scale() {
    return this.sign.scale
  }

  get defaultScale() {
    return this.sign.defaultScale
  }

  get pixelRatio() {
    return this.sign.pixelRatio
  }

  get isPreview() {
    return this.mode === DrawableMode.Preview
  }

  get isPrint() {
    return this.mode === DrawableMode.Print
  }

  get isEngraving() {
    return this.mode === DrawableMode.Engraving
  }

  get engravingColor() {
    const { thickness } = this.state

    if (thickness === 0.8) {
      return "rgb(255, 5, 0)"
    }

    if (this.signType === "contour_cut") {
      return "rgb(0, 0, 255)"
    }

    switch (this.state.color) {
      case "#eed895":
        return "rgb(255, 2, 0)"
      case "#e8cca1":
        return "rgb(255, 3, 0)"
      case "#be9261":
        return "rgb(255, 4, 0)"
    }

    return "rgb(255, 1, 0)"
  }

  get engravingSnapColor() {
    const { thickness } = this.state

    if (thickness === 0.8) {
      return "rgb(20, 120, 20)"
    }

    return "rgb(20, 120, 0)"
  }

  get engravingTextColor() {
    const { thickness, color } = this.state

    if (thickness === 0.8) {
      return "#303030"
    }

    // red background
    if (color === "#c4271d") {
      return "rgb(10,10,10)"
    }

    return "rgb(0,0,0)"
  }

  get engravingStrokeWidth() {
    return this.debugEngraving ? Calc.mmToPx(0.2) : Calc.mmToPx(0.01)
  }

  get engravingPadding() {
    const { shape } = this.state
    let padding = this.laserAdjustment * 2

    if (this.hasBorder) {
      padding += this.engravingBorderOverflow
    }

    if (shape === "ellipse") {
      padding -= Calc.mmToPx(0.1)
    }

    if (shape === "bulge") {
      padding *= 2
    }

    return padding
  }

  get engravingBottomExtra() {
    const { fixmethod } = this.state

    if (fixmethod === "table_stand") {
      return Calc.mmToPx(3)
    }

    return 0
  }

  get engravingOutsideExtra() {
    const { fixmethod } = this.state

    if (fixmethod === "table_stand") {
      return Calc.mmToPx(3)
    }

    return 0
  }

  get fixMethodMargin() {
    let side = 0
    let top = 0

    if (this.externalSides === true || this.externalTop === true) {
      const gap = Calc.mmToPx(this.externalGap)

      if (this.externalSides === true) {
        side = gap
      }

      if (this.externalTop === true) {
        top = gap
      }
    }

    return { side, top }
  }

  get externalTop(): boolean {
    return this.fixmethod ? this.fixmethod.externalTop : false
  }

  get externalBottom(): boolean {
    return this.fixmethod ? this.fixmethod.externalBottom : false
  }

  get externalSides(): boolean {
    return this.fixmethod ? this.fixmethod.externalSides : false
  }

  get externalGap(): number {
    return this.fixmethod ? this.fixmethod.externalGap : 0
  }

  get externalTopPadding(): number {
    return this.fixmethod ? this.fixmethod.externalTopPadding : 0
  }

  get externalBottomPadding(): number {
    return this.fixmethod ? this.fixmethod.externalBottomPadding : 0
  }

  get externalLeftPadding(): number {
    return this.fixmethod ? this.fixmethod.externalLeftPadding : 0
  }

  get externalRightPadding(): number {
    return this.fixmethod ? this.fixmethod.externalRightPadding : 0
  }

  get visualPadding() {
    return this.fixmethod ? this.fixmethod.visualPadding : { top: 0, bottom: 0, left: 0, right: 0 }
  }

  get borderWidth() {
    const { dualColor, widthPx, heightPx, border } = this.state

    const borderMin = dualColor ? 1.5 : 2.5
    const borderMultiplicand = Math.min(widthPx, heightPx)
    let width = 0

    switch (border) {
      case "thin":
        width = Math.max(borderMultiplicand * 0.042, Calc.mmToPx(borderMin))
        break
      case "thick":
        width = Math.max(borderMultiplicand * 0.083, Calc.mmToPx(borderMin))
        break
    }

    return +width.toFixed(3)
  }

  get hasBorder() {
    return ["thin", "thick"].includes(this.state.border)
  }

  get strokeWidth() {
    const { color, border } = this.state

    if (this.hasBorder) {
      const safariHack = this.isPreview ? this.safariBorderHack : 1
      return this.borderWidth / safariHack
    }

    if (this.signType === "acryl_sign" && color === "#f0f0f0" && border === "none") {
      return 2 / this.defaultScale
    }

    if (this.isPrint && this.signType === "sticker" && this.state.shape === "contour") {
      return 25
    }

    if (this.sign.signType !== "brass_sign") {
      return 1 / this.defaultScale / this.safariBorderHack
    }

    return 0
  }

  get stroke() {
    const { color, border, borderColor } = this.state

    if (this.signType === "acryl_sign" && color === "#f0f0f0" && border === "none") {
      return "#ebebeb"
    }

    if (this.hasBorder) {
      return borderColor
    }

    return "#cccccc"
  }

  get fixmethodCount() {
    return this.fixmethod?.count || 0
  }

  get cutlineWidth() {
    if (this.isEngraving) {
      return this.debugEngraving ? Calc.mmToPx(0.2) : Calc.mmToPx(0.01)
    }

    const strokeWidth = this.signType === "sticker" ? 0.1 : 0.3

    return Calc.mmToPx(strokeWidth)
  }

  get production() {
    return this.sign.production
  }

  get signCanvas() {
    return this.sign.canvas
  }

  get isCustomHoleTabOpened() {
    return this.sign.displayHoleDimensions
  }

  get centerX() {
    return this.sign.centerX
  }

  get centerY() {
    return this.sign.centerY
  }

  /**
   * This is for both sides total so half of this value for one side
   */
  get printPaddingWidth() {
    if (this.production !== "print") {
      return 0
    }

    if (
      ["color_badge", "brass_sign", "rollup"].includes(this.signType) ||
      (this.signType === "label_plates" && this.state.badgeName) ||
      this.state.shape === "contour"
    ) {
      return 0
    }

    return Calc.mmToPx(6)
  }

  private get drawBackplate() {
    return this.signType === "acryl_alu_sign"
  }

  private get glassBorderWidth() {
    if (this.signType === "acryl_alu_sign") {
      return 2
    }

    return 0
  }

  private get glassBorderStroke() {
    if (this.signType === "acryl_alu_sign") {
      return "#c3cfd6"
    }

    return "none"
  }

  private get safariBorderHack() {
    const { badgeName, shape } = this.state
    const safari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    const pixelRatio = this.sign.pixelRatio
    return badgeName !== "paris" && shape === "ellipse" && safari && pixelRatio > 1 ? this.sign.scale * pixelRatio : 1
  }

  private get printCutlineColor() {
    return "#FF69B4"
  }

  private formAutoSizePadding(): number {
    if (metaNameSign(this.signType)) {
      switch (this.state.shape) {
        case "ellipse":
        case "stop":
          return this.state.widthPx / 9
        default:
          return 0
      }
    }

    const textMult =
      this.state.objects
        .map(obj => {
          if ((obj as SignTextState).text) {
            return (obj as SignTextState).text.split("\n").length
          }

          return 0
        })
        .sort()
        .reverse()[0] || 0

    let triangleBorder = false

    for (const obj of this.state.objects) {
      if (obj.border && obj.border.shape && obj.border.shape === "triangle") {
        triangleBorder = true
        break
      }
    }

    switch (this.state.shape) {
      case "arrow_left":
      case "arrow_right":
        return 0 + 30 * textMult
      case "ellipse":
        return 30 + 30 * textMult
      case "stop":
        return 45 + 30 * textMult
      case "thorny":
        return this.state.widthPx / 6 + 15 * textMult
      case "round":
        return triangleBorder ? 30 : 0
      default:
        return 0
    }
  }

  private fixmethodTopDrawables() {
    const drawables = this.isEngraving ? this.fixmethod?.engravingDrawable() : this.fixmethod?.topDrawable()

    if (drawables) {
      drawables.forEach(drawable => {
        this.ensureClasses(drawable, "preview")
      })
    }

    return drawables
  }

  private ensureClasses(obj: fabric.Object | undefined | null, klass: DrawableClasses) {
    if (!obj) {
      return
    }

    if (obj instanceof fabric.Group) {
      for (const element of obj.getObjects()) {
        this.ensureClasses(element, klass)
      }
    } else {
      const drawable = obj as DrawableMixin
      if (drawable.addClass && drawable.classes.length === 0) {
        drawable.addClass(klass)
      }
    }
  }

  private addClasses(obj: fabric.Object | undefined | null, klass: DrawableClasses) {
    if (!obj) {
      return
    }

    if (obj instanceof fabric.Group) {
      for (const element of obj.getObjects()) {
        const drawable = element as DrawableMixin
        drawable.addClass(klass)
      }
    }
  }

  private disableFabricCaching(obj: fabric.Object | undefined | null) {
    if (!obj) {
      return
    }

    obj.set({ objectCaching: false })

    if (obj instanceof fabric.Group) {
      for (const element of obj.getObjects()) {
        element.set({ objectCaching: false })

        if (element instanceof fabric.Group) {
          for (const subelement of element.getObjects()) {
            subelement.set({ objectCaching: false })
          }
        }
      }
    }
  }

  private get offset() {
    if (this.signType === "acryl_alu_sign") {
      return { x: 6, y: -6 }
    }

    return { x: 0, y: 0 }
  }

  /*
   * Form parts are returned as paths in a group with no visual properties applied.
   * Every part (base, border, cutline, etc) has the appropriate class applied.
   * Here we look them up and apply the right look based on sign state and render target.
   */
  private decorateByClass(obj: DrawableMixin): DrawableMixin {
    const { color, borderColor, thickness } = this.state
    const objects = [obj]

    if (obj instanceof fabric.Group) {
      objects.push(...(obj.getObjects() as DrawableMixin[]))
    }

    for (const base of objects.filter(o => o.classes.includes("base"))) {
      this.disableFabricCaching(base)

      if (base.group) {
        base.group.clipPath = this.sign.clipShape()
      }

      base.set({
        fill: this.isEngraving ? "transparent" : color,
        stroke: "transparent",
      })

      const borderWidth = this.production === "cut" ? this.borderWidth : this.borderWidth * 2

      if (this.hasBorder) {
        base.set({
          stroke: this.isEngraving ? (thickness === 0.8 ? "#303030" : "black") : borderColor,
          strokeWidth: borderWidth,
        })
      } else if (this.isPreview) {
        base.set("stroke", this.stroke)
        base.set(
          "strokeWidth",
          this.signType === "contour_cut" || this.state.shape === "contour" ? 0 : this.strokeWidth
        )
      } else {
        base.set({
          stroke: color,
          strokeWidth: this.printPaddingWidth / 2,
        })
      }

      if (
        this.isPrint &&
        this.signType === "sticker" &&
        this.state.shape === "contour" &&
        obj instanceof fabric.Group
      ) {
        base.set({
          stroke: color,
          strokeWidth: this.strokeWidth,
          top: (base.top || 0) - this.strokeWidth / 2,
          left: (base.left || 0) - this.strokeWidth / 2,
        })
      }

      if (color === "none") {
        base.set({ fill: "transparent" })
      }

      if (this.signType === "vinyl_text" && !this.isPreview && !this.hasBorder) {
        base.set({ fill: "transparent", stroke: "transparent" })
      }

      if (
        (this.isPreview || this.isPrint) &&
        (["plastic_sign", "name_sign"].includes(this.signType) ||
          (this.signType == "label_plates" &&
            ["regular", "brass", "stainless", "aluminium"].includes(this.state.material)))
      ) {
        if (color === "#daa520") {
          base.set(
            "fill",
            new fabric.Gradient({
              coords: {
                x1: -(base.width || 0) / 2,
                y1: 0,
                x2: (base.width || 0) / 2,
                y2: 0,
              },
              colorStops: [
                { offset: 0.2, color: "#c49323" },
                { offset: 1, color: "#d2a633" },
              ],
            })
          )
        }

        if (color === "#bbbbbb") {
          base.set(
            "fill",
            new fabric.Gradient({
              coords: {
                x1: -(base.width || 0) / 2,
                y1: 0,
                x2: (base.width || 0) / 2,
                y2: 0,
              },
              colorStops: [
                { offset: 0.2, color: "#a3a7aa" },
                { offset: 1, color: "#b3bfc2" },
              ],
            })
          )
        }

        if (color === "#c27268") {
          base.set(
            "fill",
            new fabric.Gradient({
              coords: {
                x1: -(base.width || 0) / 2,
                y1: 0,
                x2: (base.width || 0) / 2,
                y2: 0,
              },
              colorStops: [
                { offset: 0.2, color: "#a8675f" },
                { offset: 1, color: "#b87a72" },
              ],
            })
          )
        }
      }

      if (this.signType === "acryl_alu_sign") {
        base.set({
          fill: "transparent",
        })

        if (this.isPrint && !this.hasBorder) {
          base.set({
            stroke: "transparent",
          })
        }
      }
    }

    for (const offsetDrawable of objects.filter(o => o.classes.includes("offsetdrawable"))) {
      offsetDrawable.set({
        fill: color,
        left: (offsetDrawable.left || 0) + this.offset.x,
        top: (offsetDrawable.top || 0) + this.offset.y,
      })
    }

    for (const glassBorder of objects.filter(o => o.classes.includes("glassborder"))) {
      glassBorder.set({ fill: "transparent", stroke: this.glassBorderStroke, strokeWidth: this.glassBorderWidth })
    }

    for (const baseDouble of objects.filter(o => o.classes.includes("borderdouble"))) {
      baseDouble.set({ fill: "transparent" })

      if (!this.hasBorder) {
        baseDouble.set({ strokeWidth: 0 })
      }
    }

    for (const shadow of objects.filter(o => o.classes.includes("shadowbase"))) {
      shadow.addClass("preview")

      if (color === "none") {
        shadow.set({ fill: "transparent", strokeWidth: 0 })

        continue
      }

      shadow.set({
        fill: this.hasBorder ? borderColor : color,
        stroke: "transparent",
      })

      if (this.signType == "acryl_alu_sign") {
        shadow.set({
          fill: "transparent",
        })
      }

      if (this.isPreview && !["vinyl_text", "contour_cut"].includes(this.signType)) {
        let offset = 4
        let blur = 12
        const scale = this.sign.defaultScale

        offset = offset / scale / this.pixelRatio
        blur = blur / scale / this.pixelRatio

        // todo: on ios the shadow gets large and clipped unless object caching is turned on
        if (ios()) {
          blur = Math.min(blur, 10)
          offset = blur / 3
        }

        shadow.set("shadow", new fabric.Shadow({ offsetX: offset, offsetY: offset, blur, color: "rgba(0,0,0,0.3)" }))
      }
    }

    for (const overflow of objects.filter(o => o.classes.includes("pdfoverflow"))) {
      this.disableFabricCaching(overflow)
      const flangedOverlay = overflow.classes.includes("flangedpath") && overflow.classes.includes("borderdouble")

      if (this.isPreview || flangedOverlay) {
        overflow.set({
          fill: "transparent",
          stroke: "transparent",
        })
      } else {
        overflow.set({
          fill: this.hasBorder ? borderColor : color,
          stroke: this.hasBorder ? borderColor : color,
          strokeWidth: this.printPaddingWidth / 2,
        })
      }
    }

    for (const flangedhole of objects.filter(o => o.classes.includes("flangedhole"))) {
      this.disableFabricCaching(flangedhole)

      if (this.isPreview) {
        flangedhole.set({
          fill: "transparent",
          stroke: "transparent",
        })
      } else {
        flangedhole.set({
          fill: "transparent",
          stroke: "#8D40C0",
          strokeWidth: this.cutlineWidth,
        })
      }
    }

    const cutlineColor = this.isPrint ? this.printCutlineColor : this.engravingColor

    for (const cutline of objects.filter(o => o.classes.includes("cutline"))) {
      this.disableFabricCaching(cutline)

      if (this.isPreview || this.production === "cut") {
        cutline.set({
          fill: "transparent",
          strokeWidth: 0,
          // stroke: cutlineColor,
          // strokeWidth: this.cutlineWidth,
        })
      } else {
        cutline.set({
          fill: "transparent",
          stroke: cutlineColor,
          strokeWidth: this.cutlineWidth,
          spotStroke: "cut_",
        })
      }
    }

    for (const snappable of objects.filter(o => o.classes.includes("cutline") && o.classes.includes("snappable"))) {
      this.disableFabricCaching(snappable)

      if (this.isPreview || this.production === "cut") {
        snappable.set({
          fill: "transparent",
          strokeWidth: 0,
        })
      } else {
        snappable.set({
          fill: "transparent",
          stroke: this.engravingSnapColor,
          strokeWidth: this.cutlineWidth,
        })
      }
    }

    for (const printAreaCutline of objects.filter(o => o.classes.includes("printareacutline"))) {
      this.disableFabricCaching(printAreaCutline)

      printAreaCutline.set({
        fill: "transparent",
        strokeWidth: 0,
      })
    }

    for (const gappedCutline of objects.filter(o => o.classes.includes("gapped"))) {
      this.disableFabricCaching(gappedCutline)

      gappedCutline.set({
        fill: "transparent",
        strokeWidth: 0,
      })
    }

    return obj
  }

  private createForm(state: SignState) {
    const { shape, badgeName, doubleSided, side } = state

    if (shape === "rect" || shape === "round") {
      return new RectForm(this)
    } else if (shape === "rotated_rect") {
      return new RotatedRectForm(this)
    } else if (["thorny", "stop"].includes(shape)) {
      return new StarForm(this)
    } else if (shape === "arrow_left") {
      return new ArrowLeftForm(this)
    } else if (shape === "arrow_right") {
      return new ArrowRightForm(this)
    } else if (shape === "direction_left") {
      return doubleSided && side == 1 ? new DirectionRightForm(this) : new DirectionLeftForm(this)
    } else if (shape === "direction_right") {
      return doubleSided && side == 1 ? new DirectionLeftForm(this) : new DirectionRightForm(this)
    } else if (shape === "triangle") {
      return new TriangleForm(this)
    } else if (shape === "bulge") {
      return new BulgeForm(this)
    } else if (shape === "round_incut") {
      return new RoundIncutForm(this)
    } else if (shape === "ellipse") {
      return badgeName === "paris" ? new ParisForm(this) : new EllipseForm(this)
    } else if (shape === "contour") {
      return new ContourForm(this)
    } else if (shape === "rounded_top") {
      return new RoundedTopForm(this)
    } else if (shape === "rounded_sides") {
      return new RoundedSidesForm(this)
    } else if (shape === "arched_top") {
      return new ArchedTopForm(this)
    } else if (shape === "curved_top") {
      return new CurvedTopForm(this)
    } else if (shape === "arched_top_bottom") {
      return new ArchedTopBottomForm(this)
    }

    throw new Error(`Unknown form: ${shape}`)
  }

  private createFixmethod(state: SignState) {
    const { fixmethod } = state

    if (fixmethod === "table_stand") {
      return new TableStandFixmethod(this)
    } else if (fixmethod === "wall_profile") {
      return new WallProfileFixmethod(this)
    } else if (fixmethod === "wall_distance") {
      return new WallDistanceFixmethod(this)
    } else if (fixmethod === "eyelets") {
      return new EyeletsFixmethod(this)
    } else if (fixmethod === "flag_mount") {
      return new FlagMountFixmethod(this)
    } else if (fixmethod === "hanging") {
      return new HangingFixmethod(this)
    } else if (fixmethod === "ceiling_mount") {
      return new CeilingMountFixmethod(this)
    } else if (fixmethod === "military") {
      return new MilitaryFixmethod(this)
    } else if (["pole", "pole_flanged"].includes(fixmethod || "")) {
      return new PoleFixmethod(this)
    } else if (fixmethod === "stand") {
      return new RollupFixmethod(this)
    } else if (fixmethod === "suction_cups") {
      return new SuctionCupsFixmethod(this)
    } else if (fixmethod === "s_hook") {
      return new SHookFixmethod(this)
    } else if (fixmethod === "keyring") {
      return new KeyringFixmethod(this)
    } else if (fixmethod === "ball_chain") {
      return new BallChainFixmethod(this)
    } else if (fixmethod === "cable_label") {
      return new CableLabelFixmethod(this)
    } else if (fixmethod === "custom_holes") {
      return new CustomHolesFixmethod(this)
    } else if (this.signType === "thermal_transfer_label") {
      return new CableTiesFixmethod(this)
    } else if (["screw", "tape_and_screw", "screw_capped"].includes(fixmethod || "")) {
      switch (this.signType) {
        case "plastic_sign":
          return new ScrewPlasticFixmethod(this)
        case "wood_sign":
          return new ScrewWoodFixmethod(this)
        case "aluminium_sign":
        case "acryl_sign":
          return new ScrewAluFixmethod(this)
        case "forex_sign":
          return new ScrewForexFixmethod(this)
        case "solid_plastic_sign":
          return new ScrewSolidPlasticFixmethod(this)
        case "brass_sign":
          return new ScrewBrassFixmethod(this)
        case "label_plates":
          switch (state.material) {
            case "regular":
            case "aluminium":
              return new ScrewPlasticFixmethod(this)
            case "aluminium_lacquered":
              return new ScrewAluFixmethod(this)
            case "stainless":
              return new ScrewStainlessFixmethod(this)
            case "brass":
            case "nickel":
              return new ScrewBrassFixmethod(this)
          }
          break
        case "ecoboard":
          return new ScrewEcoboardFixmethod(this)
        case "stainless_steel_sign":
          return new ScrewStainlessFixmethod(this)
      }
    }

    return null
  }

  private createPattern(state: SignState) {
    return SignPattern.create(state, this)
  }

  private createBaseOverlay(state: SignState) {
    return this.overlay.createBaseOverlay(state)
  }
}
